import DateParse from "@/classes/helpers/DateParse";
import { OrganizationService } from "@/classes/services/OrganizationService";
import Organization from "@/classes/Organization";
import { SubscriptionService } from "@/classes/services/SubscriptionService";
import { UserService } from "@/classes/services/UserService";
import User from "@/classes/User";
import Domain from "@/classes/Domain";
import { AlertType } from "@/classes/enums/AlertType";
import ErrorMessageHelper from "@/classes/helpers/ErrorMessageHelper";
import { AxiosError } from "axios";
import ProductLicense from "@/classes/ProductLicense";
import AccessKey from "@/classes/AccessKey";

const organizationService = new OrganizationService();
const subscriptionService = new SubscriptionService();
const userService = new UserService();

const state = {
  organizations: [],
  organization: new Organization(),
  productLicenses: [],
  productLicense: new ProductLicense(),
  organizationDomains: [],
  organizationAdmin: new User(),
  isStepCompleted: false,
  isLoading: false,
  stepNumber: 1,
  editedIndex: -1,
  orgAdminRole: "",
  alertType: "",
  alertText: "",
  accessKeys: [],
  orgImageUri: "",
  orgSettings: {},
};

const getters = {
  getOrganizations: () => {
    return state.organizations;
  },

  getProductLicenses: () => {
    return state.productLicenses;
  },

  getDomains: () => {
    return state.organizationDomains;
  },

  getIsStepCompleted: () => {
    return state.isStepCompleted;
  },

  getStepNumber: () => {
    return state.stepNumber;
  },

  getIsLoading: () => {
    return state.isLoading;
  },

  getAlertTypes: () => {
    return state.alertType;
  },

  getAlertText: () => {
    return state.alertText;
  },

  getAccessKeys: () => {
    return state.accessKeys;
  },
  getOrgImageUri: () => {
    return state.orgImageUri;
  },
  getOrgSettings: () => {
    return state.orgSettings;
  },
};

const mutations = {
  setOrganization: (state, payload) => {
    state.organization = payload;
  },

  setProductLicense: (state, payload) => {
    state.productLicense = payload;
  },

  setOrganizationAdmin: (state, payload) => {
    state.organizationAdmin = payload;
  },

  setStepNumber: (state, payload) => {
    state.stepNumber = payload;
  },

  setOrganizationAdminRole: (state, payload) => {
    state.orgAdminRole = payload;
  },

  setDomains: (state, payload) => {
    state.organizationDomains = payload;
  },

  setAlertType(state, payload) {
    state.alertType = payload;
  },

  setAlertText(state, payload) {
    state.alertText = payload;
  },

  setIsStepCompleted(state, payload) {
    state.isStepCompleted = payload;
  },

  setAccessKey(state, payload) {
    state.accessKey = payload;
  },
  setOrgImageUri(state, payload) {
    state.orgImageUri = payload;
  },
  setOrgSettings(state, payload) {
    state.orgSettings = payload;
  },
};

const actions = {
  async getOrganizationsAsync() {
    startLoading();
    organizationService
      .getOrganizations()
      .then((response: any) => {
        state.organizations = response.data.items.map((organization: any) => {
          organization.createdAt = DateParse.ParseTimeFromUTC(
            organization.createdAt
          );
          organization.updatedAt = DateParse.ParseTimeFromUTC(
            organization.updatedAt
          );
          return organization;
        });
      })
      .finally(() => {
        stopLoading();
      });
  },

  async createOrganization({ commit, dispatch }, payload) {
    startLoading();
    dispatch("clearAlertAsync");
    commit("setOrganization", payload);
    organizationService
      .isOrganizationExists(state.organization)
      .then(() => {
        stopLoading();
        dispatch("clearAlertAsync");
        dispatch(
          "setErrorAlertAsync",
          `${state.organization.name} already exists.`
        );
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        startLoading();
        if (state.organization.resellerId === "") {
          state.organization.resellerId = null;
        }
        organizationService
          .createOrganization(state.organization)
          .then((response: any) => {
            stopLoading();
            state.organization.id = response.data.id;
            organizationService
              .updateOrganizationStatus(state.organization)
              .then(() => {
                dispatch(
                  "setSuccessAlertAsync",
                  `${state.organization.name} has been created`
                );
                goToNextStep();
              })
              .catch((error: AxiosError<Error>) => {
                stopLoading();
                dispatch("clearAlertAsync");
                dispatch("setErrorAlertAsync", error);
              });
          })
          .catch((error: AxiosError<Error>) => {
            stopLoading();
            dispatch("clearAlertAsync");
            dispatch("setErrorAlertAsync", error);
          });
      });
  },

  async updateOrganization({ commit, dispatch }, payload) {
    startLoading();
    dispatch("clearAlertAsync");
    commit("setOrganization", payload);
    organizationService
      .updateOrganization(state.organization)
      .then(() => {
        stopLoading();
        organizationService
          .updateOrganizationStatus(state.organization)
          .then(() => {
            if (Object.keys(state.orgSettings).length !== 0) {
              state.orgSettings.allowCreatingPublicLibrary =
                state.organization.allowCreatingPublicLibrary;
                state.orgSettings.allowSiloLibrary =
                state.organization.allowSiloLibrary;
              organizationService.updateOrganizationSettings(
                state.organization.id,
                state.orgSettings
              );
            }
            dispatch(
              "setSuccessAlertAsync",
              `${state.organization.name} has been updated`
            );
            dispatch("getOrganizations");
          })
          .catch((error: AxiosError<Error>) => {
            stopLoading();
            dispatch("clearAlertAsync");
            dispatch("setErrorAlertAsync", error);
          });
      })
      .catch((error: AxiosError<Error>) => {
        stopLoading();
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      });
  },

  /** Retreives all of the subscriptions if they exist for an organization in edit mode */
  // This should be renamed....
  async editSubscription({ commit, dispatch }, payload) {
    startLoading();
    dispatch("clearAlertAsync");
    commit("setOrganization", payload);
    subscriptionService
      .getEntitlementsByOrgId(state.organization.id)
      .then((response: any) => {
        state.productLicenses = response.data.map((productLicense: any) => {
          productLicense.subscriptionStartDate = DateParse.ParseTimeFromUTC(
            productLicense.subscriptionStartDate
          );
          productLicense.subscriptionEndDate = DateParse.ParseTimeFromUTC(
            productLicense.subscriptionEndDate
          );
          productLicense.updatedSerialNumber = productLicense.serialNumber;
          productLicense.shouldUpdateSerialNumber = false;
          return productLicense;
        });
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      })
      .finally(() => {
        stopLoading();
      });
  },

  /** upsertProductLicensesAsync takes perform a request to update or create all of the subscription records. */
  async upsertProductLicensesAsync({ commit, dispatch }, payload) {
    startLoading();
    dispatch("clearAlertAsync");
    for (const request of payload) {
      commit("setProductLicense", request);
      await subscriptionService
        .updateEntitlements(request)
        .then(() => {
          dispatch(
            "setSuccessAlertAsync",
            "Product licenses have been updated."
          );
          state.stepNumber = 3;
        })
        .catch((error: AxiosError<Error>) => {
          dispatch("clearAlertAsync");
          dispatch("setErrorAlertAsync", error);
        })
        .finally(() => {
          stopLoading();
        });
    }

    await subscriptionService
      .getEntitlementsByOrgId(state.organization.id)
      .then((response: any) => {
        state.productLicenses = response.data.map((productLicense: any) => {
          productLicense.subscriptionStartDate = DateParse.ParseTimeFromUTC(
            productLicense.subscriptionStartDate
          );
          productLicense.subscriptionEndDate = DateParse.ParseTimeFromUTC(
            productLicense.subscriptionEndDate
          );
          productLicense.updatedSerialNumber = productLicense.serialNumber;
          productLicense.shouldUpdateSerialNumber = false;
          return productLicense;
        });
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      })
      .finally(() => {
        stopLoading();
      });
  },

  async getDomains({ dispatch }) {
    startLoading();
    organizationService
      .getDomains(state.organization)
      .then((response: any) => {
        const domains = response.data.items.map((datum: any) => {
          const domain = new Domain(datum.name);
          domain.organizationId = datum.organizationId;
          domain.id = datum.id;
          return domain;
        });
        state.organizationDomains = domains;
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      })
      .finally(() => {
        stopLoading();
      });
  },

  async createDomain({ dispatch }, payload) {
    startLoading();
    organizationService
      .createDomain(state.organization.id, payload)
      .then(() => {
        dispatch("getDomains");
        dispatch("clearAlertAsync");
        dispatch("setSuccessAlertAsync", "Domain created");
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      })
      .finally(() => {
        stopLoading();
      });
  },

  async removeDomain({ dispatch }, payload) {
    startLoading();
    organizationService
      .removeDomain(payload)
      .then(() => {
        dispatch("getDomains");
        dispatch("clearAlertAsync");
        dispatch("setSuccessAlertAsync", "Domain removed");
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      })
      .finally(() => {
        stopLoading();
      });
  },

  async completeDomainStep() {
    goToNextStep();
  },

  async createOrganizationAdmin({ commit, dispatch }, payload) {
    startLoading();
    dispatch("clearAlertAsync");
    commit("setOrganizationAdmin", payload);
    userService
      .createOrganizationAdmin(state.organizationAdmin, state.organization)
      .then((response: any) => {
        userService
          .assignOrganizationAdmin(response.data.id, {
            roleIds: [state.orgAdminRole],
          })
          .then(() => {
            // ESOTERIC resetting the stepNumber is not appearing to work properly yet.
            // We are forcing a reload programmatically so the user doesn't' have to.
            dispatch(
              "setSuccessAlertAsync",
              `${state.organizationAdmin.firstName} ${state.organizationAdmin.lastName} has been assigned Organization Admin.`
            );
            dispatch("getAllOrganizations");
          })
          .catch((error: AxiosError<Error>) => {
            dispatch("clearAlertAsync");
            dispatch("setErrorAlertAsync", error);
          });
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      })
      .finally(() => {
        stopLoading();
      });
  },

  async getOrgAdminRole({ commit }) {
    userService.getOrganizationRoles().then((response: any) => {
      const role = response.data.find(
        (role) => role.name === "Organization Admin"
      ).id;
      commit("setOrganizationAdminRole", role);
    });
  },

  async setErrorAlertAsync({ commit }, error?: AxiosError<Error>) {
    commit("setAlertType", AlertType.ERROR);
    commit(
      "setAlertText",
      error.response ? ErrorMessageHelper.createErrorMessage(error) : error
    );
  },

  async setSuccessAlertAsync({ commit }, message) {
    commit("setAlertType", AlertType.SUCCESS);
    commit("setAlertText", message);
  },

  async clearAlertAsync({ commit, dispatch }) {
    commit("setAlertType", AlertType.EMPTY);
    commit("setAlertText", "");
  },

  async getAccessKeys({ dispatch }) {
    if (state.organization.id) {
      startLoading();
      organizationService
        .getAccessKeys(state.organization.id)
        .then((response: any) => {
          const accesskeys = response.data.map((datum: any) => {
            let exp = DateParse.ParseTimeFromUTC(datum.expirationDate);
            const accessKey = new AccessKey(
              datum.keyType,
              datum.accessKey,
              exp
            );
            return accessKey;
          });
          state.accessKeys = accesskeys;
        })
        .catch((error: AxiosError<Error>) => {
          dispatch("clearAlertAsync");
          dispatch("setErrorAlertAsync", error);
        })
        .finally(() => {
          stopLoading();
        });
    }
  },

  async generateAccessKey({ dispatch }, payload: AccessKey) {
    startLoading();
    organizationService
      .generateAccessKey(state.organization.id, payload)
      .then((response: any) => {
        dispatch("getAccessKeys");
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      })
      .finally(() => {
        stopLoading();
      });
  },

  async updateAccessKey({ dispatch }, payload: AccessKey) {
    startLoading();
    organizationService
      .updateAccessKey(
        state.organization.id,
        payload.keyType,
        payload.expirationDate
      )
      .then((response: any) => {
        dispatch("getAccessKeys");
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      })
      .finally(() => {
        stopLoading();
      });
  },

  async uploadOrgImage({ dispatch }, payload) {
    startLoading();
    organizationService
      .uploadOrgImage(state.organization.id, payload)
      .then((response: any) => {
        dispatch("setSuccessAlertAsync", "Image upload successful.");
      })
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      })
      .finally(() => {
        stopLoading();
      });
  },

  async getOrganizationSettings({ dispatch }) {
    if (state.organization.id) {
      startLoading();
      organizationService
        .getOrganizationSettings(state.organization.id)
        .then((response: any) => {
          if (Object.keys(response.data).length !== 0) {
            state.organization.allowCreatingPublicLibrary =
              response.data.allowCreatingPublicLibrary;
              state.organization.allowSiloLibrary =
              response.data.allowSiloLibrary;
            state.orgSettings = response.data;
          } else {
            organizationService
              .createCMSOrganization(state.organization.id)
              .then((response: any) => {
                if (Object.keys(response.data).length !== 0) {
                  state.organization.allowCreatingPublicLibrary =
                    response.data.settings.allowCreatingPublicLibrary;
                    state.organization.allowSiloLibrary =
                    response.data.settings.allowSiloLibrary;
                  state.orgSettings = response.data.settings;
                }
              });
          }
        })

        .catch((error: AxiosError<Error>) => {
          dispatch("clearAlertAsync");
          dispatch("setErrorAlertAsync", error);
        })
        .finally(() => {
          stopLoading();
        });
    }
  },
  async generateAutoRenewalReport({ dispatch }) {
    dispatch(
      "setSuccessAlertAsync",
      "An email with the auto renewal report will be sent in few minutes."
    );
    organizationService
      .generateAutoRenewalReport()
      .then((response: any) => {})
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      });
  },

  async generateConcurrencyLicenseUsageReport({ dispatch }, payload) {
    dispatch(
      "setSuccessAlertAsync",
      "An email with the Concurrency License Usage report will be sent in  few minutes."
    );
    organizationService
      .generateConcurrencyLicenseUsageReport(
        payload.organizationId,
        payload.days,
        payload.productIds
      )
      .catch((error: AxiosError<Error>) => {
        dispatch("clearAlertAsync");
        dispatch("setErrorAlertAsync", error);
      });
  },
};

// Helper Methods
const goToNextStep = () => {
  state.stepNumber++;
};

const startLoading = () => {
  state.isLoading = true;
};

const stopLoading = () => {
  state.isLoading = false;
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
